import type {
  ProductType,
  ProductVariant,
  TechSpecs,
  HeroVideo,
  HeroContent,
  QuantityDiscount,
} from '@/entities/product';
import colors from 'src/app/theme/colors';
import { defaultBenefits, defaultCustomBlocks } from '../../product/lib/productDefaults';
import type {
  ShopifyProduct,
  ShopifyMetafield,
  ShopifyMediaVideo,
} from '../model/shopifyProductService';

function getMetafieldValue(metafields: ShopifyMetafield[], key: string): string | null {
  const metafield = metafields.find((m) => m.key === key && m.namespace === 'custom');
  return metafield?.value || null;
}

function getMetafieldJSON<T>(metafields: ShopifyMetafield[], key: string, fallback: T): T {
  const value = getMetafieldValue(metafields, key);
  if (!value) return fallback;

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function getColorHex(colorName: string): string {
  const colorLower = colorName.toLowerCase();

  if (colorLower === 'white') return colors.white;
  if (colorLower === 'black') return colors.black;

  return colors.white;
}

function getProductType(shopifyProduct: ShopifyProduct): 'card' | 'stand' | 'plate' {
  const handle = shopifyProduct.handle.toLowerCase();
  const title = shopifyProduct.title.toLowerCase();
  const productType = shopifyProduct.productType.toLowerCase();

  if (handle.includes('card') || title.includes('card') || productType.includes('card')) {
    return 'card';
  }
  if (handle.includes('stand') || title.includes('stand') || productType.includes('stand')) {
    return 'stand';
  }
  if (handle.includes('plate') || title.includes('plate') || productType.includes('plate')) {
    return 'plate';
  }

  return 'card';
}

function parseDiscountsFromTags(tags: string[]): QuantityDiscount[] {
  const discounts: QuantityDiscount[] = [];

  tags.forEach((tag) => {
    if (!tag.startsWith('discount:')) return;

    const parts = tag.split(':');
    if (parts.length < 3) return;

    const minQuantity = parseInt(parts[1] || '', 10);
    const discountPercent = parseInt(parts[2] || '', 10);
    const discountPrice = parts[3] ? parseFloat(parts[3]) : undefined;

    if (!isNaN(minQuantity) && !isNaN(discountPercent)) {
      discounts.push({
        minQuantity,
        discountPercent,
        ...(discountPrice && !isNaN(discountPrice) ? { discountPrice } : {}),
      });
    }
  });

  return discounts.sort((a, b) => a.minQuantity - b.minQuantity);
}

export function transformShopifyProduct(shopifyProduct: ShopifyProduct): ProductType {
  if (!shopifyProduct.handle || !shopifyProduct.title) {
    console.error(
      `[ProductTransformer] Invalid Shopify product: missing handle or title`,
      shopifyProduct,
    );
    throw new Error(`Invalid Shopify product: missing handle or title`);
  }

  const basePrice = parseFloat(shopifyProduct.priceRange.minVariantPrice.amount);

  if (isNaN(basePrice) || basePrice <= 0) {
    console.error(
      `[ProductTransformer] Invalid price for product ${shopifyProduct.handle}: ${basePrice}`,
    );
    throw new Error(`Invalid price for product ${shopifyProduct.handle}: ${basePrice}`);
  }

  const variantsByColor = new Map<string, ProductVariant>();

  shopifyProduct.variants.forEach((shopifyVariant) => {
    const colorOption = shopifyVariant.selectedOptions.find(
      (opt) => opt.name.toLowerCase() === 'color',
    );

    if (!colorOption) return;

    const colorValue = colorOption.value.toLowerCase();

    if (!variantsByColor.has(colorValue)) {
      const images =
        shopifyVariant.image && shopifyVariant.image.url
          ? [
              {
                id: shopifyVariant.image.id,
                type: 'image' as const,
                src: shopifyVariant.image.url,
                alt: shopifyVariant.image.altText || shopifyProduct.title,
              },
            ]
          : [];

      variantsByColor.set(colorValue, {
        color: {
          value: colorValue,
          label: colorOption.value,
          hex: getColorHex(colorValue),
        },
        images,
      });
    }
  });

  const commonImages = shopifyProduct.images
    .filter((img) => img.url)
    .map((img, index) => ({
      id: img.id || String(index),
      type: 'image' as const,
      src: img.url,
      alt: img.altText || shopifyProduct.title,
    }));

  if (commonImages.length === 0) {
    console.error(
      `[ProductTransformer] Product ${shopifyProduct.handle} has no images. Product will not display correctly.`,
    );
    throw new Error(`Product ${shopifyProduct.handle} has no images`);
  }

  const techSpecs: TechSpecs = {
    size: getMetafieldValue(shopifyProduct.metafields, 'tech_specs_size') || 'N/A',
    weight: getMetafieldValue(shopifyProduct.metafields, 'tech_specs_weight') || 'N/A',
    material:
      getMetafieldValue(shopifyProduct.metafields, 'tech_specs_material') || 'Waterproof matte PVC',
    chip:
      getMetafieldValue(shopifyProduct.metafields, 'tech_specs_chip') ||
      'NXP NTAG213 (13.56 MHz HF)',
    compatibility:
      getMetafieldValue(shopifyProduct.metafields, 'tech_specs_compatibility') ||
      'iOS 13+ & Android 5.0+',
  };

  let heroVideo: HeroVideo | undefined;

  const shopifyVideo = shopifyProduct.media?.find((m) => m.__typename === 'Video') as
    | ShopifyMediaVideo
    | undefined;

  if (shopifyVideo && shopifyVideo.sources && shopifyVideo.sources.length > 0) {
    const webmSource = shopifyVideo.sources.find(
      (s) => s.mimeType === 'video/webm' || s.format === 'webm',
    );
    const mp4Source = shopifyVideo.sources.find(
      (s) => s.mimeType === 'video/mp4' || s.format === 'mp4',
    );

    const primarySource = webmSource || mp4Source || shopifyVideo.sources[0];
    const fallbackSource = mp4Source && webmSource ? mp4Source : undefined;

    heroVideo = {
      src: {
        base: primarySource?.url || '',
        md: primarySource?.url || '',
      },
      ...(fallbackSource
        ? {
            srcFallback: {
              base: fallbackSource.url,
              md: fallbackSource.url,
            },
          }
        : {}),
    };
  } else {
    const videoMobile = getMetafieldValue(shopifyProduct.metafields, 'hero_video_mobile');
    const videoDesktop = getMetafieldValue(shopifyProduct.metafields, 'hero_video_desktop');
    const videoMobileFallback = getMetafieldValue(
      shopifyProduct.metafields,
      'hero_video_mobile_fallback',
    );
    const videoDesktopFallback = getMetafieldValue(
      shopifyProduct.metafields,
      'hero_video_desktop_fallback',
    );

    if ((videoMobile && videoMobile.trim()) || (videoDesktop && videoDesktop.trim())) {
      heroVideo = {
        src: {
          base: videoMobile && videoMobile.trim() ? videoMobile : videoDesktop || '',
          md: videoDesktop && videoDesktop.trim() ? videoDesktop : videoMobile || '',
        },
        ...((videoMobileFallback && videoMobileFallback.trim()) ||
        (videoDesktopFallback && videoDesktopFallback.trim())
          ? {
              srcFallback: {
                base:
                  videoMobileFallback && videoMobileFallback.trim()
                    ? videoMobileFallback
                    : videoDesktopFallback || '',
                md:
                  videoDesktopFallback && videoDesktopFallback.trim()
                    ? videoDesktopFallback
                    : videoMobileFallback || '',
              },
            }
          : {}),
      };
    } else {
      const productType = getProductType(shopifyProduct);
      const defaultVideos: Record<'card' | 'stand' | 'plate', HeroVideo> = {
        card: {
          src: {
            base: '/store-products/google-reviews-card/hero-card-mobile.webm',
            md: '/store-products/google-reviews-card/hero-card-desktop.webm',
          },
          srcFallback: {
            base: '/store-products/google-reviews-card/hero-card-mobile.mp4',
            md: '/store-products/google-reviews-card/hero-card-desktop.mp4',
          },
        },
        stand: {
          src: {
            base: '/store-products/google-review-stand/hero-stand-mobile.webm',
            md: '/store-products/google-review-stand/hero-stand-desktop.webm',
          },
          srcFallback: {
            base: '/store-products/google-review-stand/hero-stand-mobile.mp4',
            md: '/store-products/google-review-stand/hero-stand-desktop.mp4',
          },
        },
        plate: {
          src: {
            base: '/store-products/google-review-plate/hero-plate-mobile.webm',
            md: '/store-products/google-review-plate/hero-plate-desktop.webm',
          },
          srcFallback: {
            base: '/store-products/google-review-plate/hero-plate-mobile.mp4',
            md: '/store-products/google-review-plate/hero-plate-desktop.mp4',
          },
        },
      };

      heroVideo = defaultVideos[productType];
    }
  }

  const benefitsFromMetafield = getMetafieldJSON<string[]>(
    shopifyProduct.metafields,
    'benefits',
    defaultBenefits,
  );

  const howItWorksDescription =
    getMetafieldValue(shopifyProduct.metafields, 'how_it_works_description') ||
    shopifyProduct.description;

  const productType = getProductType(shopifyProduct);
  const heroMainHeading = getMetafieldValue(shopifyProduct.metafields, 'hero_main_heading');
  const heroSubHeading = getMetafieldValue(shopifyProduct.metafields, 'hero_sub_heading');
  const heroDescription = getMetafieldValue(shopifyProduct.metafields, 'hero_description');

  let heroContent: Partial<HeroContent> | undefined;
  if (heroMainHeading || heroSubHeading || heroDescription) {
    heroContent = {
      ...(heroMainHeading ? { mainHeading: heroMainHeading } : {}),
      ...(heroSubHeading ? { subHeading: heroSubHeading } : {}),
      ...(heroDescription ? { description: heroDescription } : {}),
    };
  }

  const customBlocksFromMetafield = getMetafieldJSON<Array<{ title: string; content: string }>>(
    shopifyProduct.metafields,
    'custom_blocks',
    [],
  );
  const customBlocks =
    customBlocksFromMetafield.length > 0 ? customBlocksFromMetafield : defaultCustomBlocks;

  let quantityDiscounts = getMetafieldJSON<QuantityDiscount[]>(
    shopifyProduct.metafields,
    'quantity_discounts',
    [],
  );

  if (quantityDiscounts.length === 0) {
    quantityDiscounts = parseDiscountsFromTags(shopifyProduct.tags);
  }

  const transformedProduct = {
    slug: shopifyProduct.handle,
    name: shopifyProduct.title,
    type: productType,
    description: shopifyProduct.description,
    price: basePrice,
    variants: Array.from(variantsByColor.values()),
    commonImages,
    benefits: benefitsFromMetafield,
    techSpecs,
    customBlocks,
    howItWorksDescription,
    ...(heroVideo ? { heroVideo } : {}),
    ...(heroContent ? { heroContent } : {}),
    ...(quantityDiscounts.length > 0 ? { quantityDiscounts } : {}),
  };

  return transformedProduct;
}

export function transformShopifyProducts(shopifyProducts: ShopifyProduct[]): ProductType[] {
  return shopifyProducts.map(transformShopifyProduct);
}
